import { Text, useToast, VStack } from '@chakra-ui/react';
import * as Sentry from '@sentry/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router';

import {
  parse,
  ValleyBankLoanAccountWithCollateralAndStats,
} from '@finley-technologies/valley-bank-utils';

import { useGetValleyBankLoanAccountQuery } from 'types/dashboard-core-gqlTypes.generated.d';

import { useCollateralAccountsSelector } from '../CollateralAccountsSelectorProvider';
import { getUnexpectedErrorMessage } from 'packages/common-frontend/utils';
import { RouteErrorBoundary } from '~/contexts/MonitoringProvider';
import {
  Alert,
  ProgressAlert,
} from '~/packages/common-frontend/components/Alert';
import { errorToastConfig } from '~/packages/common-frontend/constants';
import { CollateralTable } from '~/valleyBank/domains/Collateral/ui/HoldingsTable';
import { useLoanAccountUrlParam } from '~/valleyBank/domains/LoanAccounts/hooks/LoanAccountContext';
import { LoanAccountNotFoundAlert } from '~/valleyBank/domains/LoanAccounts/ui/Details/LoanAccountNotFoundAlert';
import { LoanAccountsDetailsTemplate } from '~/valleyBank/domains/LoanAccounts/ui/Details/LoanAccountsDetailsTemplate';
import { useDataMetadataContext } from '~/valleyBank/domains/page/hooks/DataMetadataContext';

// Fragments
const RouteBase = () => {
  const {
    setSelectedCollateralAccounts,
    selectedFinleyReceivableIds,
    setSelectedFinleyReceivableIds,
    queuedSelectedCollateralAccounts,
    setQueuedSelectedCollateralAccounts,
    queuedSelectedFinleyReceivableIds,
    setQueuedSelectedFinleyReceivableIds,
    isFilterApplied,
    setIsFilterApplied,
    setInitialSelections,
    getDisplayRefetchAlert,
  } = useCollateralAccountsSelector();

  const toast = useToast();
  const { reportingDate, isPreview, setAsOfDate } = useDataMetadataContext();
  const { loanAccountId } = useLoanAccountUrlParam();

  const [
    loanAccount,
    setLoanAccount,
  ] = useState<ValleyBankLoanAccountWithCollateralAndStats | null>(null);
  const [
    isLoanAccountLoadingOrParsing,
    setIsLoanAccountLoadingOrParsing,
  ] = useState<boolean>(true); // account for the time it takes between loanAccount data returning and parsing + setting the data into the loanAccount state

  const {
    loading: isLoading,
    error: isError,
    refetch: refetchValleyBankLoanAccount,
  } = useGetValleyBankLoanAccountQuery({
    /**
     *  Programmatically added onError handler.
     * @onError-TODO: Explicitly handle errors for this fetch
     */
    onError: (err) => {
      setIsLoanAccountLoadingOrParsing(false);

      errorToastConfig.title = getUnexpectedErrorMessage(err);
      toast(errorToastConfig);
    },
    variables: {
      reportingDate,
      isPreview,
      loanAccountNumber: loanAccountId || '',
      selectedFinleyReceivableIds:
        isFilterApplied && selectedFinleyReceivableIds
          ? Array.from(selectedFinleyReceivableIds)
          : undefined,
    },
    onCompleted: (res) => {
      setIsLoanAccountLoadingOrParsing(false);

      try {
        const loanAccountJson = res.valleyBankLoanAccount?.loanAccount;
        const newLoanAccount = parse.parseValleyBankLoanAccountWithCollateralAndStats(
          loanAccountJson ?? '',
        );
        if (newLoanAccount) {
          setAsOfDate(newLoanAccount.asOfDate);
          setLoanAccount(newLoanAccount);
        }
      } catch (err) {
        Sentry.captureException(err);
      }
    },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  useEffect(() => {
    setInitialSelections(loanAccount);
  }, [loanAccount, setInitialSelections]);

  const toggleReceivableSelect = useCallback(
    ({
      finleyReceivableId,
      collateralAccountNumber,
      isChecked,
    }: {
      finleyReceivableId: string;
      collateralAccountNumber: string;
      isChecked: boolean;
    }) => {
      const newSelectedCollateralAccounts = queuedSelectedCollateralAccounts
        ? new Set([...queuedSelectedCollateralAccounts])
        : new Set<string>();
      const newSelectedFinleyReceivableIds = queuedSelectedFinleyReceivableIds
        ? new Set([...queuedSelectedFinleyReceivableIds])
        : new Set<string>();

      if (!isChecked) {
        newSelectedFinleyReceivableIds.delete(finleyReceivableId);
      } else {
        newSelectedFinleyReceivableIds.add(finleyReceivableId);
      }

      const parentCollateralAccountDetails = loanAccount?.collateralAccounts.find(
        (collateralAccount) =>
          collateralAccount.accountNumber === collateralAccountNumber,
      );
      const allFinleyReceivableIdsForCollateralAccount = (
        parentCollateralAccountDetails?.receivables ?? []
      ).map((receivable) => receivable.finley_receivable_id);

      const areAnyReceivablesSelectedForParentCollateralAccount = allFinleyReceivableIdsForCollateralAccount.some(
        (receivableId) => newSelectedFinleyReceivableIds.has(receivableId),
      );

      if (!areAnyReceivablesSelectedForParentCollateralAccount) {
        newSelectedCollateralAccounts.delete(collateralAccountNumber);
      } else {
        newSelectedCollateralAccounts.add(collateralAccountNumber);
      }

      setQueuedSelectedFinleyReceivableIds(newSelectedFinleyReceivableIds);
      setQueuedSelectedCollateralAccounts(newSelectedCollateralAccounts);
    },
    [
      queuedSelectedFinleyReceivableIds,
      setQueuedSelectedFinleyReceivableIds,
      loanAccount?.collateralAccounts,
      queuedSelectedCollateralAccounts,
      setQueuedSelectedCollateralAccounts,
    ],
  );

  const refetchLoanAccount = useCallback(() => {
    refetchValleyBankLoanAccount({
      reportingDate,
      isPreview,
      loanAccountNumber: loanAccountId || '',
      selectedFinleyReceivableIds:
        queuedSelectedFinleyReceivableIds &&
        Array.from(queuedSelectedFinleyReceivableIds),
    });
    setIsFilterApplied(true);
    // promote the queued to selected
    setSelectedCollateralAccounts(queuedSelectedCollateralAccounts);
    setSelectedFinleyReceivableIds(queuedSelectedFinleyReceivableIds);
  }, [
    isPreview,
    loanAccountId,
    reportingDate,
    refetchValleyBankLoanAccount,
    setIsFilterApplied,
    setSelectedCollateralAccounts,
    setSelectedFinleyReceivableIds,
    queuedSelectedCollateralAccounts,
    queuedSelectedFinleyReceivableIds,
  ]);

  const displayRefetchAlert = useMemo(
    () => getDisplayRefetchAlert({ loanAccount, isLoading }),
    [getDisplayRefetchAlert, isLoading, loanAccount],
  );

  return (
    <>
      <LoanAccountsDetailsTemplate
        selectedTab="holdings"
        displayRefetchAlert={displayRefetchAlert}
        refetchLoanAccount={refetchLoanAccount}
      >
        <VStack spacing="40px" m="40px" alignItems="flex-start">
          {isLoading && !loanAccount && (
            <ProgressAlert>Loading loan account</ProgressAlert>
          )}
          {!isLoading && !loanAccount && isError && (
            <Alert>
              <Text>Error loading loan account</Text>
            </Alert>
          )}
          {!isLoanAccountLoadingOrParsing && !isLoading && !loanAccount && (
            <LoanAccountNotFoundAlert />
          )}
          {loanAccount && (
            <CollateralTable
              collateralAccounts={loanAccount.collateralAccounts}
              isDataError={false}
              isDataLoading={false}
              showAllocation
              queuedSelectedFinleyReceivableIds={
                queuedSelectedFinleyReceivableIds
              }
              toggleReceivableSelect={toggleReceivableSelect}
              pageType="loanAccounts"
            />
          )}
        </VStack>
      </LoanAccountsDetailsTemplate>
    </>
  );
};

// Exports
export const Route = () => (
  <RouteErrorBoundary
    fallback={({ message, showErrorToast }) => {
      showErrorToast(message);
      return <Navigate to="../" />;
    }}
  >
    <RouteBase />
  </RouteErrorBoundary>
);
